// useMe — GET /api/auth/v1/me. Enabled only when there's a session AND the
// resolved tenant matches the session's tenant. AC8 forbids ANY /me call when
// the host is unknown; AC6 forbids it for stale cross-tenant sessions.
//
// Path moved 2026-05-06: was /api/lyse/v1/auth/me; see useTenantFromHost.ts
// header for the relocation context.

import { useQuery } from "@tanstack/react-query";
import type { Me } from "./types.js";
import { useAuthStore } from "./store.js";
import { getAuthBaseUrl } from "./config.js";
import { fetchWithRefresh } from "./fetchWithRefresh.js";

async function fetchMe(token: string, tenantSlug: string): Promise<Me> {
  // SB6-AUTH-REFRESH-01 — raw-fetch wrapper transparently handles
  // 401 → refresh → replay. /me itself is the canonical "is your
  // session still valid?" probe, so an expired access token here
  // would otherwise force a re-login even though the refresh token
  // is still good.
  const res = await fetchWithRefresh(`${getAuthBaseUrl()}/me`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "X-Tenant-ID": tenantSlug,
    },
  });
  if (!res.ok) {
    const err = new Error(`/me failed: ${String(res.status)}`) as Error & {
      status?: number;
    };
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as Me;
}

export function useMe(): {
  data: Me | undefined;
  isPending: boolean;
  isError: boolean;
} {
  const session = useAuthStore((s) => s.session);
  const tenant = useAuthStore((s) => s.tenant);
  const tenantMatches =
    !!session && !!tenant && session.tenant.tenantSlug === tenant.tenantSlug;

  const initialData = session?.user;
  const accessToken = session?.accessToken;
  const tenantSlug = tenant?.tenantSlug;
  const query = useQuery<Me>({
    queryKey: ["auth", "me", accessToken],
    queryFn: (): Promise<Me> => {
      if (!accessToken || !tenantSlug) {
        throw new Error("useMe queryFn invoked without session/tenant");
      }
      return fetchMe(accessToken, tenantSlug);
    },
    enabled: !!session && !!tenant && tenantMatches,
    staleTime: 60_000,
    retry: false,
    ...(initialData ? { initialData } : {}),
  });

  return {
    data: query.data,
    isPending: query.isPending,
    isError: query.isError,
  };
}
